import { useState } from "react";
import { Globe2, Lock, UserPlus, Trash2 } from "lucide-react";
import { userReportsApi } from "../../api";
import { useAuth } from "../../hooks/useAuth";
import { useAsync } from "../../hooks";
import { Button, LoadingText, Modal } from "../ui";
import type { AppUser } from "../../types/user";
import type { ReportShare, ReportSubscriber, UserReport } from "../../types/reports";

type ShareReportModalProps = {
  report: UserReport;
  onClose: () => void;
  onSuccess: () => void;
};

export default function ShareReportModal({ report, onClose, onSuccess }: ShareReportModalProps) {
  const { user: currentUser } = useAuth();
  const [shares, setShares] = useState<ReportShare[]>(report.shares ?? []);
  const [search, setSearch] = useState("");

  const { data: allUsers } = useAsync<AppUser[]>(
    () => userReportsApi.listSharingUsers().then((r) => (Array.isArray(r.data) ? r.data : [])),
    [],
  );
  const { data: subscribers, loading: subscribersLoading } = useAsync<ReportSubscriber[]>(
    () => userReportsApi.listSubscribers(report.id).then((r) => r.data),
    [report.id],
  );

  const sharedIds = new Set(shares.map((s) => s.shared_with_id));

  async function addShare(userId: string) {
    await userReportsApi.share(report.id, userId);
    const res = await userReportsApi.get(report.id);
    setShares(res.data.shares ?? []);
    onSuccess();
  }

  async function removeShare(shareId: string) {
    await userReportsApi.removeShare(report.id, shareId);
    setShares((prev) => prev.filter((s) => s.id !== shareId));
    onSuccess();
  }

  const eligible = (allUsers ?? []).filter(
    (u) => u.id !== currentUser?.id && !sharedIds.has(u.id) &&
      (u.full_name.toLowerCase().includes(search.toLowerCase()) ||
       u.email.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <Modal
      onClose={onClose}
      title="Manage access"
      subtitle={report.name}
      footer={<Button variant="secondary" onClick={onClose}>Close</Button>}
    >
      <div className="mb-4 flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
        {report.is_public ? <Globe2 size={16} className="mt-0.5 shrink-0 text-blue-600" /> : <Lock size={16} className="mt-0.5 shrink-0 text-gray-500" />}
        <div>
          <p className="text-sm font-medium text-gray-900">{report.is_public ? "Public to Brian" : "Private"}</p>
          <p className="text-xs text-gray-500">{report.is_public ? "Anyone signed in can find and preview this report." : "Only you and people shared below can access this report."}</p>
        </div>
      </div>

      {report.is_public && (
        <div className="mb-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Subscribers ({report.subscriber_count ?? subscribers?.length ?? 0})</p>
          {subscribersLoading ? (
            <LoadingText>Loading subscribers...</LoadingText>
          ) : (subscribers?.length ?? 0) === 0 ? (
            <p className="rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-400">No subscribers yet</p>
          ) : (
            <div className="max-h-40 space-y-1 overflow-y-auto">
              {subscribers!.map((subscription) => (
                <div key={subscription.id} className="rounded-lg bg-gray-50 px-3 py-2">
                  <p className="text-sm font-medium text-gray-900">{subscription.subscriber.full_name}</p>
                  <p className="text-xs text-gray-500">{subscription.subscriber.email} · Subscribed {new Date(subscription.created_at).toLocaleDateString()}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {shares.length > 0 && (
        <div className="mb-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Direct shares</p>
          <div className="space-y-1">
            {shares.map((s) => (
              <div key={s.id} className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2">
                <div>
                  <p className="text-sm font-medium text-gray-900">{s.shared_with.full_name}</p>
                  <p className="text-xs text-gray-500">{s.shared_with.email}</p>
                </div>
                <button
                  type="button"
                  onClick={() => removeShare(s.id)}
                  aria-label={`Remove ${s.shared_with.full_name}`}
                  className="text-gray-400 hover:text-red-600"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Share directly</p>
      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search by name or email..."
        className="mb-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
      />
      <div className="max-h-48 space-y-1 overflow-y-auto">
        {eligible.map((u) => (
          <div key={u.id} className="flex items-center justify-between rounded-lg px-3 py-2 hover:bg-gray-50">
            <div>
              <p className="text-sm font-medium text-gray-900">{u.full_name}</p>
              <p className="text-xs text-gray-500">{u.email}</p>
            </div>
            <Button size="sm" onClick={() => addShare(u.id)}>
              <UserPlus size={11} />
              Share
            </Button>
          </div>
        ))}
        {eligible.length === 0 && search && (
          <p className="px-3 py-2 text-sm text-gray-400">No users found</p>
        )}
      </div>
    </Modal>
  );
}
